import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import axiosConn from "config/axiosConn";
import "./SwachhataDashboard.css";
import { AWARENESS_TYPES, COMPETITIONS, REDUCTION_ACTIVITIES, awarenessLabel, competitionLabel, reductionLabel, binsLabel, compostLabel } from "../Swachhata/swachhataSurvey";

/*
 * /admin/swachhata-hi-seva — Swachhata Hi Seva 2026 command centre.
 *
 *   1. Overview   — how many States / UTs (districts, for a state) are active,
 *                   with the state-wise table of BOTH tracks; click to drill.
 *   2. Students   — public quiz: trend, score spread (certificate at ≥ 3/10),
 *                   classes, badges, top schools, hardest questions, register.
 *   3. Schools    — SWM report form: practices, activities, composting, list.
 *
 * Central admin (role 1): national → state → district. State admin (role 4):
 * locked to its own state (enforced server-side as well).
 *
 * Every aggregate endpoint is cached server-side for ~2 minutes, so opening
 * this page is cheap even with lakhs of records; lists are paginated.
 */

export const T = { green: "#138a47", green2: "#2fb457", orange: "#f08a24", blue: "#2a6fdb", teal: "#0f9f8f", violet: "#7c5cd6", red: "#d6453d", slate: "#8a9a91" };
const CLASSES = ["6", "7", "8", "9", "10", "11", "12"];

// State / district table columns (after the name). One list drives the
// table header, the Excel sheet and the PDF so all three read the same.
const AREA_COLUMNS = [
  { key: "students", label: "Students who took the quiz", hint: "completed the online quiz", bar: true, get: (r) => r.students },
  { key: "certificates", label: "Certificates issued", hint: "students who passed the quiz", get: (r) => r.certificates },
  { key: "avgScore", label: "Average quiz score", hint: "out of 10", get: (r) => (r.students ? r.avgScore : null) },
  { key: "studentSchools", label: "Schools of these students", hint: "schools the quiz students study in", get: (r) => r.studentSchools },
  { key: "reports", label: "Schools that submitted a report", hint: "SWM report form filled by the school", bar: true, get: (r) => r.reports },
  { key: "people", label: "People in school activities", hint: "students + teachers + community, as reported by schools", get: (r) => (r.reportStudents || 0) + (r.reportTeachers || 0) + (r.reportCommunity || 0) },
];

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : Number(n).toLocaleString("en-IN"));
const compact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `${(v / 1e7).toFixed(v >= 1e8 ? 1 : 2).replace(/\.0+$/, "")} Cr`;
  if (v >= 1e5) return `${(v / 1e5).toFixed(v >= 1e6 ? 1 : 2).replace(/\.0+$/, "")} L`;
  return v.toLocaleString("en-IN");
};
const pct = (n, d) => (d ? Math.round(((n || 0) / d) * 1000) / 10 : 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const fmtDay = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "");
const titleCase = (s) => String(s || "").toLowerCase().replace(/(^|[\s(&/-])([a-z])/g, (m, p, c) => p + c.toUpperCase());
const yn = (v) => (v === "yes" ? "Yes" : v === "no" ? "No" : "");
const fmtDuration = (s) => (s == null ? "—" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);

// ─── icons (inline, currentColor) ────────────────────────────────────────────
export const I = {
  map: <path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5V4l-6 2.5L9 4Zm0 0v13m6-10.5v13" />,
  student: <><path d="m12 4 10 5-10 5L2 9l10-5Z" /><path d="M6 11v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" /></>,
  cert: <><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M7 9h10M7 12.5h6" /><circle cx="16.5" cy="17.5" r="2.5" /><path d="m15.2 19.6-.7 2.4 2-1 2 1-.7-2.4" /></>,
  school: <><path d="M3 21h18M5 21V10l7-5 7 5v11" /><path d="M9.5 21v-5h5v5M12 9.5v.01" /></>,
  report: <><path d="M7 3h7l5 5v13H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></>,
  users: <><circle cx="9" cy="8" r="3.2" /><path d="M3 20c.6-3.4 3-5.5 6-5.5s5.4 2.1 6 5.5" /><path d="M16 5.2a3 3 0 0 1 0 5.6M18 14.8c1.7.8 2.8 2.6 3 5.2" /></>,
  refresh: <><path d="M20 11a8 8 0 0 0-14.3-4.9L4 8" /><path d="M4 3v5h5M4 13a8 8 0 0 0 14.3 4.9L20 16" /><path d="M20 21v-5h-5" /></>,
  back: <path d="M15 5 8 12l7 7" />,
  download: <><path d="M12 4v11m0 0-4.5-4.5M12 15l4.5-4.5" /><path d="M4 18v2h16v-2" /></>,
  search: <><circle cx="11" cy="11" r="6.5" /><path d="m20 20-4.2-4.2" /></>,
  chevron: <path d="m9 6 6 6-6 6" />,
  bin: <><path d="M4 7h16M9 7V4.5h6V7M6 7l1 13h10l1-13" /><path d="M10 11v6M14 11v6" /></>,
  leaf: <><path d="M5 19c8 0 14-6 14-15-9 0-15 6-15 14" /><path d="M5 19c2.5-4.5 6-8 10-10" /></>,
  target: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r="1" /></>,
  clock: <><circle cx="12" cy="12" r="8.5" /><path d="M12 7.5V12l3 2" /></>,
  lock: <><rect x="5" y="10.5" width="14" height="10" rx="2" /><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" /></>,
  chip: <><rect x="6" y="6" width="12" height="12" rx="2" /><path d="M9.5 9.5h5v5h-5zM9 2v4M15 2v4M9 18v4M15 18v4M2 9h4M2 15h4M18 9h4M18 15h4" /></>,
  plug: <><path d="M9 2v6M15 2v6M6 8h12v4a6 6 0 0 1-12 0V8Z" /><path d="M12 18v4" /></>,
  camera: <><path d="M4 8h3l2-3h6l2 3h3v11H4V8Z" /><circle cx="12" cy="13" r="3.5" /></>,
};
export const Icon = ({ name, size = 20 }) => (
  <svg className="swx-ic" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{I[name]}</svg>
);

// ─── building blocks ─────────────────────────────────────────────────────────
export function StatTile({ icon, tone, label, value, sub, foot, loading, i = 0 }) {
  return (
    <div className="swx-stat" style={{ "--tone": tone, "--i": i }}>
      <div className="swx-stat__top">
        <span className="swx-stat__icon"><Icon name={icon} size={22} /></span>
        <span className="swx-stat__label">{label}</span>
      </div>
      {loading ? <div className="swx-skel swx-skel--value" /> : <div className="swx-stat__value">{value}</div>}
      {sub && <div className="swx-stat__sub">{sub}</div>}
      {foot != null && <div className="swx-stat__foot"><i style={{ width: `${Math.max(0, Math.min(100, foot))}%` }} /></div>}
    </div>
  );
}

// Every standalone admin report is reached from the reports dashboard, so each
// one needs a way back to it. The browser Back button is not enough: a report
// opened from a bookmark, an emailed link or a drill-down has nowhere to go
// back TO, which is why this navigates to the dashboard rather than to -1.
export function BackToReports({ label = "Back to Reports", to = "/admin/environment-day-dashboard" }) {
  const navigate = useNavigate();
  return (
    <button type="button" className="swx-backbar" onClick={() => navigate(to)}>
      <Icon name="back" size={16} />
      {label}
    </button>
  );
}

export function SectionHead({ id, tone, icon, eyebrow, title, desc, actions }) {
  return (
    <div className="swx-shead" id={id} style={{ "--tone": tone }}>
      <div className="swx-shead__main">
        <span className="swx-shead__icon"><Icon name={icon} size={24} /></span>
        <div>
          <div className="swx-shead__eyebrow">{eyebrow}</div>
          <div className="swx-shead__title">{title}</div>
          {desc && <div className="swx-shead__desc">{desc}</div>}
        </div>
      </div>
      {actions && <div className="swx-shead__actions">{actions}</div>}
    </div>
  );
}

export function Panel({ title, desc, right, children, className = "", span }) {
  return (
    <div className={`swx-panel ${className}`} style={span ? { gridColumn: `span ${span}` } : undefined}>
      {(title || right) && (
        <div className="swx-panel__head">
          <div>
            {title && <div className="swx-panel__title">{title}</div>}
            {desc && <div className="swx-panel__desc">{desc}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

export function MiniKpi({ label, value, hint, tone }) {
  return (
    <div className="swx-mini" style={{ "--tone": tone }}>
      <div className="swx-mini__value">{value}</div>
      <div className="swx-mini__label">{label}</div>
      {hint && <div className="swx-mini__hint">{hint}</div>}
    </div>
  );
}

export function Bars({ rows, total, tone, valueLabel = (r) => `${pct(r.count, total)}%`, sub = (r) => fmt(r.count) }) {
  const max = total || Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="swx-bars">
      {rows.map((r, i) => (
        <div key={r.key} className="swx-bars__row" style={{ "--i": i }}>
          <span className="swx-bars__label" title={r.label}>{r.label}</span>
          <span className="swx-bars__track"><i style={{ width: `${Math.min(100, (r.count / max) * 100)}%`, background: r.tone || tone }} /></span>
          <span className="swx-bars__val">{valueLabel(r)}<small>{sub(r)}</small></span>
        </div>
      ))}
    </div>
  );
}

export function Empty({ text, icon = "leaf" }) {
  return <div className="swx-empty"><Icon name={icon} size={26} /><span>{text}</span></div>;
}

export function Pager({ page, totalPages, total, noun, onPage, loading }) {
  return (
    <div className="swx-pager">
      <span>{loading ? "Loading…" : `${fmt(total)} ${noun}${total === 1 ? "" : "s"} · page ${fmt(page)} of ${fmt(totalPages)}`}</span>
      <div className="swx-pager__btns">
        <button type="button" disabled={page <= 1} onClick={() => onPage(1)}>«</button>
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹ Prev</button>
        <button type="button" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>Next ›</button>
        <button type="button" disabled={page >= totalPages} onClick={() => onPage(totalPages)}>»</button>
      </div>
    </div>
  );
}

export const chartBase = { fontFamily: "Inter, system-ui, sans-serif", toolbar: { show: false }, zoom: { enabled: false }, foreColor: "#5b6f63", animations: { enabled: true, easing: "easeout", speed: 500 } };
export const gridBase = { borderColor: "rgba(17,38,26,0.07)", strokeDashArray: 4, padding: { left: 4, right: 8 } };

// 401, or one of the backend's own wordings ("Please login to access this
// resource", "JSON web token is expired, try again" — sent as 400), means the
// admin session is gone, not that the statistics failed.
export const isAuthFailure = (e) =>
  e?.response?.status === 401 || /please login|session expired|json web token/i.test(e?.response?.data?.message || e?.message || "");

export const post = async (url, payload) => {
  const res = await axiosConn("post", url, payload);
  if (!res?.data?.success) throw new Error(res?.data?.message || "Request failed");
  return res.data;
};

export default function SwachhataDashboard() {
  const user = useSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const isStateAdmin = user?.role === 4;

  // filters: `draft` is what the inputs show, `applied` is what was fetched
  const empty = { state: "", district: "", from: "", to: "" };
  const [draft, setDraft] = useState(empty);
  const [applied, setApplied] = useState(empty);
  const scope = useCallback((f) => {
    const p = {};
    ["state", "district", "from", "to"].forEach((k) => { if (f[k]) p[k] = f[k]; });
    return p;
  }, []);

  const [overview, setOverview] = useState(null);
  const [students, setStudents] = useState(null);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [stateOptions, setStateOptions] = useState([]);
  const [tab, setTab] = useState("overview");

  const [areaSort, setAreaSort] = useState("students");
  const [areaQuery, setAreaQuery] = useState("");

  const fetchAll = useCallback(async (f) => {
    setLoading(true); setError(""); setExpired(false);
    const [o, s, r] = await Promise.allSettled([post("swachhata/admin/overview", scope(f)), post("swachhata/admin/students/stats", scope(f)), post("swachhata/admin/stats", scope(f))]);
    if (o.status === "fulfilled") {
      setOverview(o.value);
      if (!f.state && !o.value.scope?.locked) setStateOptions(o.value.rows.filter((x) => x.key));
    }
    if (s.status === "fulfilled") setStudents(s.value);
    if (r.status === "fulfilled") setReports(r.value);
    const failed = [o, s, r].find((x) => x.status === "rejected");
    if (failed) {
      // A dead session used to surface as a red "Please login to access this
      // resource" strip above an empty dashboard. Say it plainly instead.
      if (isAuthFailure(failed.reason)) setExpired(true);
      else setError(failed.reason?.response?.data?.message || failed.reason?.message || "Some statistics could not be loaded — please refresh.");
    }
    setLoading(false);
  }, [scope]);
  useEffect(() => { fetchAll(applied); }, [applied, fetchAll]);

  // ── student register ──
  const [sq, setSq] = useState({ search: "", klass: "", cert: "", page: 1 });
  const [sList, setSList] = useState({ rows: [], total: 0, totalPages: 1, page: 1 });
  const [sLoading, setSLoading] = useState(false);
  const [sExporting, setSExporting] = useState(false);
  const studentQuery = useCallback((f, q, extra = {}) => ({ ...scope(f), page: q.page, limit: 25, ...(q.search ? { search: q.search } : {}), ...(q.klass ? { class: q.klass } : {}), ...(q.cert ? { certificate: q.cert } : {}), ...extra }), [scope]);
  useEffect(() => {
    if (tab !== "students") return undefined;
    const t = setTimeout(async () => {
      setSLoading(true);
      try { const d = await post("swachhata/admin/students", studentQuery(applied, sq)); setSList({ rows: d.rows, total: d.total, totalPages: d.totalPages, page: d.page }); }
      catch (e) { if (isAuthFailure(e)) setExpired(true); }
      finally { setSLoading(false); }
    }, sq.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [applied, sq, studentQuery, tab]);

  // ── school register ──
  const [rq, setRq] = useState({ search: "", sortBy: "surveySubmittedAt", sortDir: "desc", page: 1 });
  const [rList, setRList] = useState({ rows: [], total: 0, totalPages: 1, page: 1 });
  const [rLoading, setRLoading] = useState(false);
  const [rExporting, setRExporting] = useState(false);
  const schoolQuery = useCallback((f, q, extra = {}) => ({ ...scope(f), page: q.page, limit: 25, sortBy: q.sortBy, sortDir: q.sortDir, ...(q.search ? { search: q.search } : {}), ...extra }), [scope]);
  useEffect(() => {
    if (tab !== "schools") return undefined;
    const t = setTimeout(async () => {
      setRLoading(true);
      try { const d = await post("swachhata/admin/schools", schoolQuery(applied, rq)); setRList({ rows: d.rows, total: d.total, totalPages: d.totalPages, page: d.page }); }
      catch (e) { if (isAuthFailure(e)) setExpired(true); }
      finally { setRLoading(false); }
    }, rq.search ? 350 : 0);
    return () => clearTimeout(t);
  }, [applied, rq, schoolQuery, tab]);

  // ── navigation between scopes ──
  const level = overview?.level || (applied.state ? "district" : "state");
  const lockedState = overview?.scope?.locked ? overview.scope.state : null;
  const apply = (next) => { setApplied(next); setDraft(next); setSq((q) => ({ ...q, page: 1 })); setRq((q) => ({ ...q, page: 1 })); };
  const drill = (row) => {
    if (!row.key) return;
    if (level === "state" && !isStateAdmin) apply({ ...applied, state: row.key, district: "" });
    else if (level === "district" && !applied.district) apply({ ...applied, district: row.key });
  };
  const scopeName = lockedState || (applied.state ? titleCase(applied.state) : "All States/UTs/ABs");
  const crumbs = [
    !isStateAdmin && { label: "India", onClick: applied.state ? () => apply({ ...applied, state: "", district: "" }) : null },
    (applied.state || lockedState) && { label: lockedState || titleCase(applied.state), onClick: applied.district ? () => apply({ ...applied, district: "" }) : null },
    applied.district && { label: titleCase(applied.district), onClick: null },
  ].filter(Boolean);

  // ── derived data ──
  const ot = overview?.totals || {};
  const minScore = overview?.certificateMinScore ?? students?.kpis?.certificateMinScore ?? 3;
  const areaWord = level === "district" ? "District" : "States/UTs/ABs";
  const areaWordPlural = level === "district" ? "Districts" : "States/UTs/ABs";
  const areaRows = useMemo(() => {
    const q = areaQuery.trim().toLowerCase();
    const rows = (overview?.rows || []).filter((r) => !q || r.name.toLowerCase().includes(q));
    const by = {
      students: (a, b) => b.students - a.students || b.reports - a.reports,
      reports: (a, b) => b.reports - a.reports || b.students - a.students,
      certificates: (a, b) => b.certificates - a.certificates || b.students - a.students,
      name: (a, b) => a.name.localeCompare(b.name),
    }[areaSort];
    return [...rows].sort(by);
  }, [overview, areaQuery, areaSort]);
  const maxStudents = Math.max(1, ...areaRows.map((r) => r.students));
  const maxReports = Math.max(1, ...areaRows.map((r) => r.reports));

  const sk = students?.kpis || {};
  const trend = students?.trend || [];
  const trendOptions = useMemo(() => ({
    chart: { ...chartBase, type: "area", stacked: false },
    colors: [T.green, T.orange],
    stroke: { curve: "smooth", width: [2.6, 2.2] },
    fill: { type: "gradient", gradient: { opacityFrom: 0.32, opacityTo: 0.02, stops: [0, 95] } },
    dataLabels: { enabled: false },
    grid: gridBase,
    legend: { position: "top", horizontalAlign: "right", fontWeight: 600, markers: { radius: 12 } },
    xaxis: { categories: trend.map((t) => t.date), labels: { formatter: fmtDay, style: { fontSize: "11px" }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
    yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v) => compact(Math.round(v)) } },
    tooltip: { shared: true, intersect: false, x: { formatter: (v, o) => new Date(trend[o?.dataPointIndex]?.date || v).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) } },
  }), [trend]);
  const trendSeries = useMemo(() => [{ name: "Students completed", data: trend.map((t) => t.students) }, { name: "Certificates", data: trend.map((t) => t.certificates) }], [trend]);

  const dist = students?.scoreDistribution || [];
  const distOptions = useMemo(() => ({
    chart: { ...chartBase, type: "bar" },
    plotOptions: { bar: { columnWidth: "62%", borderRadius: 6, distributed: true } },
    colors: dist.map((d) => (d.score >= minScore ? (d.score >= 9 ? "#0e7a3c" : d.score >= 7 ? T.green : T.green2) : "#cfd9d2")),
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: gridBase,
    xaxis: { categories: dist.map((d) => String(d.score)), title: { text: "Correct answers (out of 10)", style: { fontWeight: 600, color: "#5b6f63" } }, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { labels: { formatter: (v) => compact(Math.round(v)) } },
    annotations: { xaxis: [{ x: String(minScore), borderColor: T.orange, strokeDashArray: 0, label: { text: `Certificate from ${minScore}`, orientation: "horizontal", style: { background: T.orange, color: "#fff", fontWeight: 700 } } }] },
    tooltip: { y: { formatter: (v) => `${fmt(v)} students` } },
  }), [dist, minScore]);

  const badges = students?.badges || [];
  const badgeTones = { waste_warrior: "#0e7a3c", segregation_champion: T.teal, eco_learner: T.orange, keep_learning: T.slate };
  const donutOptions = useMemo(() => ({
    chart: { ...chartBase, type: "donut" },
    labels: badges.map((b) => b.label),
    colors: badges.map((b) => badgeTones[b.key]),
    legend: { position: "bottom", fontWeight: 600 },
    dataLabels: { enabled: false },
    stroke: { width: 3, colors: ["#fff"] },
    plotOptions: { pie: { donut: { size: "70%", labels: { show: true, total: { show: true, label: "Students", formatter: () => compact(sk.submitted || 0) }, value: { fontWeight: 800 } } } } },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [badges, sk.submitted]);

  const hardest = useMemo(() => (students?.questions || []).filter((q) => q.attempts > 0).sort((a, b) => a.accuracy - b.accuracy).slice(0, 5), [students]);

  const sv = reports?.survey || {};
  const nReports = sv.submitted || 0;
  const rTrend = reports?.trend || [];
  const rTrendOptions = useMemo(() => ({
    chart: { ...chartBase, type: "area" },
    colors: [T.blue],
    stroke: { curve: "smooth", width: 2.6 },
    fill: { type: "gradient", gradient: { opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 95] } },
    dataLabels: { enabled: false },
    grid: gridBase,
    xaxis: { categories: rTrend.map((t) => t.date), labels: { formatter: fmtDay, style: { fontSize: "11px" }, rotate: 0, hideOverlappingLabels: true }, axisBorder: { show: false }, axisTicks: { show: false }, tooltip: { enabled: false } },
    yaxis: { min: 0, forceNiceScale: true, labels: { formatter: (v) => compact(Math.round(v)) } },
    tooltip: { x: { formatter: (v, o) => new Date(rTrend[o?.dataPointIndex]?.date || v).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" }) } },
  }), [rTrend]);
  const practiceRows = [
    { key: "bins", label: "All four bins installed", count: sv.binsAllFour || 0 },
    { key: "awareness", label: "Awareness activity held", count: sv.awareness || 0 },
    { key: "audit", label: "Solid waste audit done", count: sv.wasteAudit || 0 },
    { key: "reduction", label: "Reduce / reuse / recycle", count: sv.wasteReduction || 0 },
    { key: "compost", label: "Wet waste composted", count: sv.compost?.yes || 0 },
  ];
  const withLabels = (list, options) => options.map((o) => ({ key: o.key, label: o.label, count: (list || []).find((x) => x.key === o.key)?.count || 0 })).sort((a, b) => b.count - a.count);
  const participants = (sv.students || 0) + (sv.teachers || 0) + (sv.community || 0);

  // ── exports ──
  const stamp = () => new Date().toISOString().slice(0, 10);
  const scopeFile = () => [applied.state, applied.district].filter(Boolean).map(titleCase).join("_").replace(/\s+/g, "_") || "National";
  const areaTitle = level === "district" ? `District-wise participation · ${scopeName}` : "States/UTs/ABs-wise participation";
  const areaTotals = { ...ot, avgScore: ot.avgScore };
  const exportAreas = () => {
    const toRow = (r, i) => ({
      "S.No": i == null ? "" : i + 1,
      [areaWord]: r.name,
      ...Object.fromEntries(AREA_COLUMNS.map((c) => [c.label, c.get(r) ?? ""])),
      "Certificate rate %": pct(r.certificates, r.students),
      "Students (reported by schools)": r.reportStudents || 0,
      "Teachers (reported by schools)": r.reportTeachers || 0,
      "Community (reported by schools)": r.reportCommunity || 0,
      "Schools with all four bins": r.binsAllFour ?? "",
      "Schools composting wet waste": r.compostYes ?? "",
    });
    const rows = areaRows.map(toRow);
    rows.push(toRow({ ...areaTotals, name: `Total · ${fmt(ot.areas)} ${areaWordPlural.toLowerCase()}` }, null));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, areaWordPlural.replace(/\W+/g, " ").trim());
    XLSX.writeFile(wb, `SwachhataHiSeva_${level === "district" ? "Districts" : "States_UTs_ABs"}_${scopeFile()}_${stamp()}.xlsx`);
  };
  const exportAreasPdf = () => {
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const cell = (c, r) => { const v = c.get(r); return v == null ? "—" : c.key === "avgScore" ? String(v) : fmt(v); };
    doc.setFontSize(15);
    doc.text(`Swachhata Hi Seva 2026 — ${areaTitle}`, 40, 40);
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(`Student quiz: students who took the online SWM quiz (certificate at ${minScore}/10 or more). School SWM report: schools that filled the report form and the people they report in their activities.`, 40, 58, { maxWidth: 760 });
    doc.text(`Generated ${new Date().toLocaleString("en-IN")}`, 40, 84);
    doc.autoTable({
      startY: 96,
      head: [
        [
          { content: "#", rowSpan: 2 }, { content: areaWord, rowSpan: 2 },
          { content: "Student quiz", colSpan: 4, styles: { halign: "center" } },
          { content: "School SWM report", colSpan: 2, styles: { halign: "center" } },
        ],
        AREA_COLUMNS.map((c) => `${c.label}\n(${c.hint})`),
      ],
      body: areaRows.map((r, i) => [i + 1, r.name, ...AREA_COLUMNS.map((c) => cell(c, r))]),
      foot: [["", `Total · ${fmt(ot.areas)} ${areaWordPlural.toLowerCase()}`, ...AREA_COLUMNS.map((c) => cell(c, areaTotals))]],
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [19, 138, 71], textColor: 255, fontSize: 7.5, valign: "middle" },
      footStyles: { fillColor: [15, 36, 24], textColor: 255, fontStyle: "bold" },
      columnStyles: Object.fromEntries(AREA_COLUMNS.map((_, i) => [i + 2, { halign: "right" }])),
      margin: { left: 40, right: 40 },
    });
    doc.save(`SwachhataHiSeva_${level === "district" ? "Districts" : "States_UTs_ABs"}_${scopeFile()}_${stamp()}.pdf`);
  };
  const exportStudents = async () => {
    setSExporting(true);
    try {
      const d = await post("swachhata/admin/students", studentQuery(applied, sq, { export: true, page: 1 }));
      const ws = XLSX.utils.json_to_sheet(d.rows.map((r, i) => ({
        "S.No": i + 1, "Student Name": r.name, Class: r.class, Age: r.age, "UDISE Code": r.udise_id, "School Name": r.school_name || "(not matched on UDISE+)", State: r.state, District: r.district,
        Score: r.score, "Out Of": r.total, Percent: r.percent, Badge: r.badgeLabel || "", "Certificate ID": r.certificate_id || "", "Submitted At": fmtDate(r.submitted_at), "Time Taken": fmtDuration(r.duration_sec),
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Students");
      XLSX.writeFile(wb, `SwachhataHiSeva_Students_${scopeFile()}_${stamp()}.xlsx`);
    } catch (e) { setError("Student export failed — please try again."); }
    finally { setSExporting(false); }
  };
  const exportSchools = async () => {
    setRExporting(true);
    try {
      const d = await post("swachhata/admin/schools", schoolQuery(applied, rq, { export: true, page: 1 }));
      const ws = XLSX.utils.json_to_sheet(d.rows.map((r, i) => ({
        "S.No": i + 1, "UDISE Code": r.udise_id, "School Name": r.school_name, State: r.state, District: r.district, Block: r.block || "", "School Type": r.school_type || "",
        "Head of School": r.hosName || "", Email: r.schEmail || "", Mobile: r.schMobile || "",
        "First Submitted At": fmtDate(r.survey_first_submitted_at), "Last Updated At": fmtDate(r.survey_submitted_at), "Times Updated": r.survey_updates,
        "Q1 Four Bins Installed": r.bins_installed ? binsLabel(r.bins_installed) : "", "Q2 Awareness Activity Conducted": yn(r.awareness_conducted),
        "Q2A Awareness Activity Types": (r.awareness_types || []).map(awarenessLabel).join("; "), "Q2A Other": r.awareness_other || "",
        "Q3 Competitions / Activities": (r.competitions || []).map(competitionLabel).join("; "), "Q3 Other": r.competition_other || "",
        "Q4 Students Participated": r.students_participated ?? "", "Q5 Teachers Participated": r.teachers_participated ?? "", "Q6 Community Members Participated": r.community_participated ?? "",
        "Q7 Solid Waste Audit": yn(r.waste_audit), "Q8 Waste Reduction / Reuse / Recycling": yn(r.waste_reduction), "Q8 Activities": (r.reduction_activities || []).map(reductionLabel).join("; "), "Q8 Other": r.reduction_other || "",
        "Q9 Wet Waste Composted": r.wet_waste_composted ? compostLabel(r.wet_waste_composted) : "", "Q10 Photos / Files": (r.photos || []).join("\n"),
        "Q11 Exposure Visit to SLWM Site": yn(r.exposure_visit), "Q11 Place of Visit": r.visit_place || "", "Q11 Description": r.visit_description || "",
        "Q11 Students Participated": r.visit_students ?? "", "Q11 Teachers Participated": r.visit_teachers ?? "",
        "Q11 Geo-tagged Photos": (r.visit_photos || []).map((p) => `${p.url} (${p.latitude}, ${p.longitude})`).join("\n"),
        "Certificate ID": r.certificate_id || "",
      })));
      const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "School reports");
      XLSX.writeFile(wb, `SwachhataHiSeva_SchoolReports_${scopeFile()}_${stamp()}.xlsx`);
    } catch (e) { setError("School export failed — please try again."); }
    finally { setRExporting(false); }
  };
  const sortSchools = (key) => setRq((q) => ({ ...q, page: 1, sortBy: key, sortDir: q.sortBy === key ? (q.sortDir === "asc" ? "desc" : "asc") : key === "school" || key === "state" || key === "district" ? "asc" : "desc" }));
  const sortMark = (key) => (rq.sortBy === key ? (rq.sortDir === "asc" ? " ↑" : " ↓") : "");
  const TABS = [["overview", "map", `${areaWord}-wise`], ["students", "student", "Students · quiz"], ["schools", "school", "Schools · reports"]];

  return (
    <div className="swx">
      {/* ═══ HERO ═══ */}
      <section className={`swx-hero${expired ? " swx-hero--bare" : ""}`}>
        <div className="swx-wrap">
          <div className="swx-hero__top">
            <div className="swx-hero__intro">
              <div className="swx-live"><i />Live campaign · Swachhata Hi Seva 2026</div>
              <div className="swx-hero__title">Solid Waste Management Drive</div>
              <div className="swx-hero__slogan">Swachh Paathshala: Yuva for Swachhata</div>
              <div className="swx-hero__sub">{`Student quiz and school SWM reports · ${scopeName}`}</div>
              <div className="swx-crumbs">
                {crumbs.map((c, i) => (
                  <React.Fragment key={c.label}>
                    {i > 0 && <Icon name="chevron" size={14} />}
                    {c.onClick ? <button type="button" onClick={c.onClick}>{c.label}</button> : <span>{c.label}</span>}
                  </React.Fragment>
                ))}
                {isStateAdmin && <span className="swx-crumbs__lock"><Icon name="lock" size={13} />State admin view</span>}
              </div>
            </div>
            <div className="swx-hero__actions">
              <button type="button" className="swx-btn swx-btn--glass" onClick={() => navigate("/admin/environment-day-dashboard")}><Icon name="back" size={16} />Back to Reports</button>
              <button type="button" className="swx-btn swx-btn--glass" onClick={() => fetchAll(applied)} disabled={loading}><Icon name="refresh" size={16} />{loading ? "Refreshing…" : "Refresh"}</button>
              <button type="button" className="swx-btn swx-btn--orange" onClick={() => navigate("/admin/swm-report")} title="Every question of the school SWM report, state / district-wise, with Excel and PDF"><Icon name="report" size={16} />Detailed SWM report</button>
            </div>
          </div>

          {!expired && (<>
          <div className="swx-filters">
            {!isStateAdmin && (
              <label className="swx-field">
                <span>States/UTs/ABs</span>
                <select value={draft.state} onChange={(e) => setDraft({ ...draft, state: e.target.value, district: "" })}>
                  <option value="">All States/UTs/ABs</option>
                  {stateOptions.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                  {draft.state && !stateOptions.some((r) => r.key === draft.state) && <option value={draft.state}>{titleCase(draft.state)}</option>}
                </select>
              </label>
            )}
            {(applied.state || isStateAdmin) && level === "district" && (
              <label className="swx-field">
                <span>District</span>
                <select value={draft.district} onChange={(e) => setDraft({ ...draft, district: e.target.value })}>
                  <option value="">All districts</option>
                  {(overview?.rows || []).filter((r) => r.key).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                  {draft.district && !(overview?.rows || []).some((r) => r.key === draft.district) && <option value={draft.district}>{titleCase(draft.district)}</option>}
                </select>
              </label>
            )}
            <label className="swx-field"><span>From</span><input type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} /></label>
            <label className="swx-field"><span>To</span><input type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} /></label>
            <div className="swx-filters__btns">
              <button type="button" className="swx-btn swx-btn--orange" onClick={() => apply(draft)}>Apply filters</button>
              <button type="button" className="swx-btn swx-btn--glass" onClick={() => apply(empty)}>Reset</button>
            </div>
          </div>

          <nav className="swx-tabs" role="tablist" aria-label="Sections">
            {TABS.map(([k, icon, label]) => (
              <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? "is-on" : ""} onClick={() => setTab(k)}>
                <Icon name={icon} size={16} />{label}
              </button>
            ))}
          </nav>
          </>)}
        </div>
      </section>

      <div className="swx-wrap">
        {/* ═══ HEADLINE ═══ */}
        {expired ? (
          <div className="swx-relogin">
            <span className="swx-relogin__icon"><Icon name="lock" size={26} /></span>
            <div className="swx-relogin__main">
              <b>Your admin session has ended</b>
              <span>Sign in again to see the Swachhata Hi Seva figures. Nothing has been lost — the data is still there.</span>
            </div>
            <button type="button" className="swx-btn swx-btn--orange" onClick={() => navigate("/admin/login")}>Login again</button>
          </div>
        ) : (
        <>
        <section className="swx-stats">
          <StatTile i={0} icon="map" tone={T.violet} loading={!overview} label={`${areaWordPlural} participating`} value={fmt(ot.areas)} sub={`${fmt(ot.areasWithStudents)} with students · ${fmt(ot.areasWithReports)} with reports`} />
          <StatTile i={1} icon="student" tone={T.green} loading={!overview} label="Students completed quiz" value={compact(ot.students)} sub={`Average score ${ot.avgScore ?? 0} / 10 · ${compact(ot.studentSchools)} schools`} />
          <StatTile i={2} icon="cert" tone={T.orange} loading={!overview} label="Certificates issued" value={compact(ot.certificates)} sub={`${ot.certificateRate ?? 0}% scored ${minScore} or more`} foot={ot.certificateRate} />
          <StatTile i={3} icon="report" tone={T.blue} loading={!overview} label="School SWM reports" value={compact(ot.reports)} sub={`${compact((ot.reportStudents || 0) + (ot.reportTeachers || 0) + (ot.reportCommunity || 0))} people reached`} foot={pct(ot.binsAllFour, ot.reports)} />
        </section>

        {error && <div className="swx-alert">{error}</div>}

        {/* ═══ OVERVIEW TAB ═══ */}
        {tab === "overview" && (<>
        <SectionHead
          id="swx-overview"
          tone={T.violet}
          icon="map"
          eyebrow={`${areaWord}-wise`}
          title={level === "district" ? `District-wise participation · ${scopeName}` : "States/UTs/ABs-wise participation"}
          desc={level === "district" ? "Every district of the state with students or school reports. Click a district to focus on it." : "Every State/UT/AB with students or school reports — click a row to open its districts."}
          actions={
            <div className="swx-export">
              <button type="button" className="swx-btn swx-btn--soft" onClick={exportAreas} disabled={!areaRows.length}><Icon name="download" size={16} />Excel</button>
              <button type="button" className="swx-btn swx-btn--soft" onClick={exportAreasPdf} disabled={!areaRows.length}><Icon name="download" size={16} />PDF</button>
            </div>
          }
        />
        <div className="swx-panel swx-panel--flush">
          <div className="swx-toolbar">
            <div className="swx-search"><Icon name="search" size={16} /><input type="search" placeholder={`Search ${areaWordPlural.toLowerCase()}…`} value={areaQuery} onChange={(e) => setAreaQuery(e.target.value)} /></div>
            <div className="swx-sortby">
              <span className="swx-sortby__label">Sort by</span>
              <div className="swx-seg" role="tablist" aria-label="Sort by">
                {[["students", "Students"], ["certificates", "Certificates"], ["reports", "School reports"], ["name", `${areaWord} name`]].map(([k, l]) => (
                  <button key={k} type="button" className={areaSort === k ? "is-on" : ""} onClick={() => setAreaSort(k)}>{l}</button>
                ))}
              </div>
            </div>
          </div>
          <p className="swx-legend">
            <b>Student quiz</b> — students who took the online Solid Waste Management quiz (certificate at {minScore}/10 or more).{" "}
            <b>School SWM report</b> — schools that filled the SWM report form, and the people they say took part in their activities.
          </p>
          {!overview ? (
            <div className="swx-skel-rows">{Array.from({ length: 6 }, (_, i) => <div key={i} className="swx-skel swx-skel--row" />)}</div>
          ) : areaRows.length === 0 ? (
            <Empty text="No students or school reports yet for this scope." icon="map" />
          ) : (
            <div className="swx-table-wrap swx-table-wrap--tall">
              <table className="swx-table swx-table--areas">
                <thead>
                  <tr className="swx-thgroup">
                    <th rowSpan={2} className="c-rank">#</th>
                    <th rowSpan={2}>{areaWord}</th>
                    <th colSpan={4} className="swx-thgroup--quiz">Student quiz</th>
                    <th colSpan={2} className="swx-thgroup--report">School SWM report</th>
                    <th rowSpan={2} className="c-go" aria-label="Open" />
                  </tr>
                  <tr className="swx-thsub">
                    {AREA_COLUMNS.map((c) => (
                      <th key={c.key} className={c.bar ? "c-bar" : "num"} title={c.hint}>{c.label}<small>{c.hint}</small></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {areaRows.map((r, i) => {
                    const clickable = !!r.key && ((level === "state" && !isStateAdmin) || (level === "district" && !applied.district));
                    const people = r.reportStudents + r.reportTeachers + r.reportCommunity;
                    return (
                      <tr key={r.key || `none-${i}`} className={clickable ? "is-link" : ""} onClick={() => clickable && drill(r)} style={{ "--i": Math.min(i, 20) }}>
                        <td className="c-rank"><span className={`swx-rank ${i < 3 && areaSort !== "name" ? `swx-rank--${i + 1}` : ""}`}>{i + 1}</span></td>
                        <td><div className="swx-area">{r.name}</div>{!r.key && <div className="swx-area__note">UDISE code not found on UDISE+</div>}</td>
                        <td className="c-bar">
                          <div className="swx-cellbar"><span className="swx-cellbar__track"><i style={{ width: `${(r.students / maxStudents) * 100}%` }} /></span><b>{fmt(r.students)}</b></div>
                        </td>
                        <td className="num"><b>{fmt(r.certificates)}</b><small>{`${pct(r.certificates, r.students)}%`}</small></td>
                        <td className="num">{r.students ? r.avgScore : "—"}</td>
                        <td className="num">{fmt(r.studentSchools)}</td>
                        <td className="c-bar">
                          <div className="swx-cellbar swx-cellbar--blue"><span className="swx-cellbar__track"><i style={{ width: `${(r.reports / maxReports) * 100}%` }} /></span><b>{fmt(r.reports)}</b></div>
                        </td>
                        <td className="num">{fmt(people)}</td>
                        <td className="c-go">{clickable && <Icon name="chevron" size={16} />}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td>{`Total · ${fmt(ot.areas)} ${areaWordPlural.toLowerCase()}`}</td>
                    <td className="c-bar"><b>{fmt(ot.students)}</b></td>
                    <td className="num"><b>{fmt(ot.certificates)}</b><small>{`${ot.certificateRate ?? 0}%`}</small></td>
                    <td className="num">{ot.avgScore ?? "—"}</td>
                    <td className="num">{fmt(ot.studentSchools)}</td>
                    <td className="c-bar"><b>{fmt(ot.reports)}</b></td>
                    <td className="num">{fmt((ot.reportStudents || 0) + (ot.reportTeachers || 0) + (ot.reportCommunity || 0))}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        </>)}

        {/* ═══ STUDENTS TAB ═══ */}
        {tab === "students" && (<>
        <SectionHead
          id="swx-students"
          tone={T.green}
          icon="student"
          eyebrow="Student-wise"
          title="Students — Solid Waste Management quiz · Swachh Paathshala: Yuva for Swachhata"
          desc={`Public quiz, no login: 10 random questions, certificate at ${minScore}/10 or more. Only completed quizzes are recorded.`}
          actions={<button type="button" className="swx-btn swx-btn--soft" onClick={exportStudents} disabled={sExporting || !sList.total}><Icon name="download" size={16} />{sExporting ? "Exporting…" : "Export students"}</button>}
        />
        <div className="swx-minis swx-minis--4">
          <MiniKpi tone={T.green} label="Completed the quiz" value={compact(sk.submitted)} hint={`${compact(sk.schools)} schools`} />
          <MiniKpi tone={T.orange} label="Certificates" value={compact(sk.certificates)} hint={`${sk.certificateRate ?? 0}% of students`} />
          <MiniKpi tone={T.teal} label="Average score" value={`${sk.avgScore ?? 0}/10`} hint={`${compact(sk.fullMarks)} full marks`} />
          <MiniKpi tone={T.violet} label="Average time" value={fmtDuration(sk.avgDurationSec)} />
        </div>

        <div className="swx-grid">
          <Panel span={7} title="Daily participation" desc="Quizzes completed and certificates issued per day (IST)">
            {!students ? <div className="swx-skel swx-skel--chart" /> : trend.length === 0 ? <Empty text="No completed quizzes yet." /> : (
              <ReactApexChart options={trendOptions} series={trendSeries} type="area" height={300} />
            )}
          </Panel>
          <Panel span={5} title="Score distribution" desc={`Bars in green earned a certificate (score ${minScore}+)`}>
            {!students ? <div className="swx-skel swx-skel--chart" /> : !sk.submitted ? <Empty text="No completed quizzes yet." /> : (
              <ReactApexChart options={distOptions} series={[{ name: "Students", data: dist.map((d) => d.count) }]} type="bar" height={290} />
            )}
          </Panel>
        </div>

        <details className="swx-more">
          <summary><Icon name="chevron" size={15} />More student insights — badges, classes, top schools, hardest questions</summary>
          <div className="swx-grid">
          <Panel span={4} title="Badges earned" desc="Waste Warrior 9–10 · Champion 7–8 · Eco Learner 5–6">
            {!students ? <div className="swx-skel swx-skel--chart" /> : !sk.submitted ? <Empty text="No completed quizzes yet." /> : (
              <ReactApexChart options={donutOptions} series={badges.map((b) => b.count)} type="donut" height={300} />
            )}
          </Panel>
          <Panel span={8} title="Class-wise" desc="Students per class · average score · certificates">
            {!students ? <div className="swx-skel swx-skel--chart" /> : (
              <div className="swx-classes">
                {(() => {
                  const max = Math.max(1, ...(students.classes || []).map((c) => c.count));
                  return (students.classes || []).map((c, i) => (
                    <div key={c.class} className="swx-classes__row" style={{ "--i": i }}>
                      <span className="swx-classes__chip">{c.class}</span>
                      <span className="swx-classes__track"><i style={{ width: `${(c.count / max) * 100}%` }} /></span>
                      <span className="swx-classes__val"><b>{fmt(c.count)}</b><small>{c.count ? `avg ${c.avgScore} · ${pct(c.certificates, c.count)}% cert.` : "no entries"}</small></span>
                    </div>
                  ));
                })()}
              </div>
            )}
          </Panel>
          <Panel span={6} title="Top schools by students" desc="Schools whose students took the quiz the most">
            {!students ? <div className="swx-skel swx-skel--chart" /> : !(students.topSchools || []).length ? <Empty text="No schools yet." icon="school" /> : (
              <ol className="swx-leader">
                {students.topSchools.map((s, i) => (
                  <li key={s.udise_id} style={{ "--i": i }}>
                    <span className={`swx-rank ${i < 3 ? `swx-rank--${i + 1}` : ""}`}>{i + 1}</span>
                    <div className="swx-leader__main">
                      <b>{s.school_name}</b>
                      <small>{`UDISE ${s.udise_id}${s.district ? ` · ${titleCase(s.district)}` : ""}${s.state ? `, ${titleCase(s.state)}` : ""}`}</small>
                    </div>
                    <div className="swx-leader__num"><b>{fmt(s.students)}</b><small>{`avg ${s.avgScore}`}</small></div>
                  </li>
                ))}
              </ol>
            )}
          </Panel>
          <Panel span={6} title="Where students struggle" desc="The five questions answered correctly least often">
            {!students ? <div className="swx-skel swx-skel--chart" /> : !hardest.length ? <Empty text="Question accuracy appears once quizzes come in." icon="target" /> : (
              <div className="swx-hard">
                {hardest.map((q, i) => (
                  <div key={q.id} className="swx-hard__row" style={{ "--i": i }}>
                    <div className="swx-hard__q"><span className="swx-hard__cat">{q.category}</span>{q.q}</div>
                    <div className="swx-hard__meter"><span><i style={{ width: `${q.accuracy}%` }} /></span><b>{`${q.accuracy}%`}</b></div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
          </div>
        </details>

        <div className="swx-panel swx-panel--flush">
          <div className="swx-panel__head swx-panel__head--pad">
            <div><div className="swx-panel__title">Student register</div><div className="swx-panel__desc">Search by name, school, district, UDISE code or certificate ID</div></div>
          </div>
          <div className="swx-toolbar">
            <div className="swx-search"><Icon name="search" size={16} /><input type="search" placeholder="Name, school, UDISE or SHS26-STU-… " value={sq.search} onChange={(e) => setSq({ ...sq, search: e.target.value, page: 1 })} /></div>
            <select className="swx-select" value={sq.klass} onChange={(e) => setSq({ ...sq, klass: e.target.value, page: 1 })}>
              <option value="">All classes</option>
              {CLASSES.map((c) => <option key={c} value={c}>{`Class ${c}`}</option>)}
            </select>
            <div className="swx-seg">
              {[["", "All"], ["yes", "With certificate"], ["no", `Below ${minScore}`]].map(([k, l]) => (
                <button key={k || "all"} type="button" className={sq.cert === k ? "is-on" : ""} onClick={() => setSq({ ...sq, cert: k, page: 1 })}>{l}</button>
              ))}
            </div>
          </div>
          {sList.rows.length === 0 ? <Empty text={sLoading ? "Loading students…" : "No students match these filters."} icon="student" /> : (
            <div className={`swx-table-wrap ${sLoading ? "is-busy" : ""}`}>
              <table className="swx-table">
                <thead><tr><th>Student</th><th>Class</th><th>School</th><th>District / State/UT/AB</th><th className="num">Score</th><th>Badge</th><th>Certificate</th><th>Completed</th></tr></thead>
                <tbody>
                  {sList.rows.map((r) => (
                    <tr key={r.attemptId}>
                      <td><div className="swx-person"><span className="swx-avatar">{(r.name || "?").trim().charAt(0).toUpperCase()}</span><div><b>{r.name}</b><small>{`Age ${r.age ?? "—"}`}</small></div></div></td>
                      <td><span className="swx-chip">{`Class ${r.class}`}</span></td>
                      <td className="c-wide">{r.school_name ? <><b className="swx-strong">{r.school_name}</b><small>{`UDISE ${r.udise_id}`}</small></> : <><span className="swx-muted">Not matched on UDISE+</span><small>{`UDISE ${r.udise_id}`}</small></>}</td>
                      <td>{r.district ? titleCase(r.district) : "—"}<small>{titleCase(r.state)}</small></td>
                      <td className="num"><span className={`swx-score ${r.score >= minScore ? "is-good" : "is-low"}`}>{`${r.score ?? "—"}/${r.total ?? 10}`}</span></td>
                      <td>{r.badgeLabel ? <span className={`swx-badge swx-badge--${r.badge}`}>{r.badgeLabel}</span> : "—"}</td>
                      <td>{r.certificate_id ? <a className="swx-cert" href={`/swachhata-hi-seva/verify/${r.certificate_id}`} target="_blank" rel="noopener noreferrer">{r.certificate_id}</a> : <span className="swx-muted">—</span>}</td>
                      <td className="c-nowrap">{fmtDate(r.submitted_at)}<small>{fmtDuration(r.duration_sec)}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={sList.page} totalPages={sList.totalPages} total={sList.total} noun="student" loading={sLoading} onPage={(p) => setSq({ ...sq, page: p })} />
        </div>

        </>)}

        {/* ═══ SCHOOLS TAB ═══ */}
        {tab === "schools" && (<>
        <SectionHead
          id="swx-schools"
          tone={T.blue}
          icon="school"
          eyebrow="School-wise"
          title="Schools — Solid Waste Management report · Swachh Paathshala: Yuva for Swachhata"
          desc="The Q1–Q10 report each school submits from its dashboard (bins, awareness, competitions, participation, audit, reduction, composting, photos)."
          actions={<button type="button" className="swx-btn swx-btn--soft" onClick={exportSchools} disabled={rExporting || !rList.total}><Icon name="download" size={16} />{rExporting ? "Exporting…" : "Export schools"}</button>}
        />
        <div className="swx-minis swx-minis--4">
          <MiniKpi tone={T.blue} label="Reports submitted" value={compact(nReports)} hint={`${fmt(sv.updated)} updated later`} />
          <MiniKpi tone={T.green} label="All four bins" value={`${pct(sv.binsAllFour, nReports)}%`} hint={`${fmt(sv.binsAllFour)} schools`} />
          <MiniKpi tone={T.teal} label="Composting on campus" value={`${pct(sv.compost?.yes, nReports)}%`} hint={`${fmt(sv.compost?.yes)} schools`} />
          <MiniKpi tone={T.violet} label="People reached" value={compact(participants)} hint={`${compact(sv.students)} students`} />
        </div>

        <div className="swx-grid">
          <Panel span={7} title="Reports per day" desc="Schools submitting their report for the first time (IST)">
            {!reports ? <div className="swx-skel swx-skel--chart" /> : rTrend.length === 0 ? <Empty text="No school reports yet." icon="report" /> : (
              <ReactApexChart options={rTrendOptions} series={[{ name: "Reports", data: rTrend.map((t) => t.reports) }]} type="area" height={290} />
            )}
          </Panel>
          <Panel span={5} title="Practices in place" desc="Share of reporting schools answering “Yes”">
            {!reports ? <div className="swx-skel swx-skel--chart" /> : !nReports ? <Empty text="No school reports yet." icon="bin" /> : <Bars rows={practiceRows} total={nReports} tone={T.green} />}
          </Panel>
        </div>

        <details className="swx-more">
          <summary><Icon name="chevron" size={15} />More report insights — awareness, competitions, reduction, composting</summary>
          <div className="swx-grid">
          <Panel span={4} title="Awareness activities (Q2A)" desc="Share of reporting schools">
            {!reports ? <div className="swx-skel swx-skel--chart" /> : !nReports ? <Empty text="No reports yet." /> : <Bars rows={withLabels(sv.awarenessTypes, AWARENESS_TYPES)} total={nReports} tone={T.orange} />}
          </Panel>
          <Panel span={4} title="Competitions (Q3)" desc="Share of reporting schools">
            {!reports ? <div className="swx-skel swx-skel--chart" /> : !nReports ? <Empty text="No reports yet." /> : <Bars rows={withLabels(sv.competitions, COMPETITIONS)} total={nReports} tone={T.violet} />}
          </Panel>
          <Panel span={4} title="Reduce · reuse · recycle (Q8)" desc="Share of reporting schools">
            {!reports ? <div className="swx-skel swx-skel--chart" /> : !nReports ? <Empty text="No reports yet." /> : <Bars rows={withLabels(sv.reductionActivities, REDUCTION_ACTIVITIES)} total={nReports} tone={T.teal} />}
          </Panel>
          <Panel span={12} title="Wet waste composting (Q9)" desc="Is wet waste composted within the school premises?">
            {!reports ? <div className="swx-skel swx-skel--row" /> : !nReports ? <Empty text="No reports yet." /> : (
              <>
                <div className="swx-stack">
                  {[["yes", "Yes", T.green], ["no", "No", T.red], ["na", "Not applicable", T.slate]].map(([k, l, c]) => {
                    const w = pct(sv.compost?.[k], nReports);
                    return w > 0 ? <span key={k} style={{ width: `${w}%`, background: c }} title={`${l}: ${fmt(sv.compost?.[k])}`}>{w >= 8 ? `${l} · ${w}%` : ""}</span> : null;
                  })}
                </div>
                <div className="swx-legend">
                  {[["yes", "Yes", T.green], ["no", "No", T.red], ["na", "Not applicable", T.slate]].map(([k, l, c]) => (
                    <span key={k}><i style={{ background: c }} />{`${l} · ${fmt(sv.compost?.[k])} schools`}</span>
                  ))}
                </div>
              </>
            )}
          </Panel>
          </div>
        </details>

        <div className="swx-panel swx-panel--flush">
          <div className="swx-panel__head swx-panel__head--pad">
            <div><div className="swx-panel__title">School report register</div><div className="swx-panel__desc">Every school that submitted the SWM report — all answers are in the export</div></div>
          </div>
          <div className="swx-toolbar">
            <div className="swx-search"><Icon name="search" size={16} /><input type="search" placeholder="School name, district or 11-digit UDISE" value={rq.search} onChange={(e) => setRq({ ...rq, search: e.target.value, page: 1 })} /></div>
          </div>
          {rList.rows.length === 0 ? <Empty text={rLoading ? "Loading schools…" : "No school reports match these filters."} icon="school" /> : (
            <div className={`swx-table-wrap ${rLoading ? "is-busy" : ""}`}>
              <table className="swx-table">
                <thead>
                  <tr>
                    <th className="is-sort" onClick={() => sortSchools("school")}>{`School${sortMark("school")}`}</th>
                    <th className="is-sort" onClick={() => sortSchools("district")}>{`District / State/UT/AB${sortMark("district")}`}</th>
                    <th>Four bins</th>
                    <th>Awareness</th>
                    <th className="num is-sort" onClick={() => sortSchools("students")}>{`Students${sortMark("students")}`}</th>
                    <th className="num">Teachers</th>
                    <th className="num">Community</th>
                    <th>Composting</th>
                    <th>Photos</th>
                    <th className="is-sort" onClick={() => sortSchools("surveySubmittedAt")}>{`Updated${sortMark("surveySubmittedAt")}`}</th>
                  </tr>
                </thead>
                <tbody>
                  {rList.rows.map((r) => (
                    <tr key={r.udise_id}>
                      <td className="c-wide"><b className="swx-strong">{r.school_name}</b><small>{`UDISE ${r.udise_id}${r.certificate_id ? ` · ${r.certificate_id}` : ""}`}</small></td>
                      <td>{titleCase(r.district) || "—"}<small>{titleCase(r.state)}</small></td>
                      <td><span className={`swx-yn ${r.bins_installed === "all_four" ? "is-yes" : "is-no"}`}>{r.bins_installed === "all_four" ? "All four" : "Not yet"}</span></td>
                      <td><span className={`swx-yn ${r.awareness_conducted === "yes" ? "is-yes" : "is-no"}`}>{yn(r.awareness_conducted) || "—"}</span></td>
                      <td className="num">{fmt(r.students_participated)}</td>
                      <td className="num">{fmt(r.teachers_participated)}</td>
                      <td className="num">{fmt(r.community_participated)}</td>
                      <td>{r.wet_waste_composted ? compostLabel(r.wet_waste_composted) : "—"}</td>
                      <td className="c-nowrap">{(r.photos || []).length ? r.photos.map((u, i) => <a key={u} className="swx-file" href={u} target="_blank" rel="noopener noreferrer"><Icon name="camera" size={13} />{i + 1}</a>) : "—"}</td>
                      <td className="c-nowrap">{fmtDate(r.survey_submitted_at)}{r.survey_updates ? <small>{`updated ${r.survey_updates}×`}</small> : null}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pager page={rList.page} totalPages={rList.totalPages} total={rList.total} noun="school" loading={rLoading} onPage={(p) => setRq({ ...rq, page: p })} />
        </div>

        </>)}

        <div className="swx-foot">
          {`Figures refresh every ~2 minutes${overview?.generatedAt ? ` · last built ${fmtDate(overview.generatedAt)}` : ""} · Students: public quiz records (completed only) · Schools: SWM report records`}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
